// ============================================================================
// CANCUN100 — PDFEspecial.jsx (APP 2/26 · Reserva especial: documento con dos caras)
// ----------------------------------------------------------------------------
// data = datosDocumento(nota, calc, cara)  ·  cara 'cli' = para el cliente (nunca netos; marca y contacto a elegir)
//                                             cara 'int' = interna · contabilidad (netos, margen por renglón, comisión por beneficiario, ganancia)
// ============================================================================
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import './pdfFonts';

const MORADO = '#6A35D4', TINTA = '#1C1630', GRIS = '#625B7A', CLARO = '#8A83A0', LINEA = '#ECE9F3', SUAVE = '#F1EAFE', OK = '#17A673', MAL = '#E0475B';
const F0 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const m = (n) => `${Number(n) < 0 ? '−$' : '$'}${F0.format(Math.abs(Math.round(Number(n) || 0)))}`;
const f = (d) => (d ? String(d).slice(0, 10) : '—');

const s = StyleSheet.create({
  page: { paddingTop: 34, paddingBottom: 40, paddingHorizontal: 38, fontFamily: 'DM Sans', color: TINTA, fontSize: 9.5 },
  top: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  logo: { maxHeight: 44, maxWidth: 150, objectFit: 'contain', marginBottom: 4 },
  marca: { fontSize: 16, fontWeight: 700, color: MORADO, letterSpacing: -0.3 },
  sub: { fontSize: 9, color: GRIS, marginTop: 3 },
  tag: { fontSize: 8, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#5A25B8', backgroundColor: SUAVE, paddingVertical: 3, paddingHorizontal: 8, borderRadius: 4 },
  folio: { fontSize: 18, fontWeight: 700, color: MORADO, fontFamily: 'Space Grotesk', textAlign: 'right', marginTop: 6 },
  der: { alignItems: 'flex-end' },
  contacto: { fontSize: 8.5, color: GRIS, textAlign: 'right', marginTop: 4 },
  th: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: LINEA, paddingVertical: 6, marginTop: 10 },
  thc: { fontSize: 7.5, color: CLARO, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' },
  tr: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 0.5, borderBottomColor: LINEA },
  td: { fontSize: 9.5 }, r: { textAlign: 'right' },
  tot: { marginTop: 12, alignItems: 'flex-end' },
  linea: { fontSize: 9.5, color: GRIS, marginBottom: 3 },
  grande: { fontSize: 14, fontWeight: 700, color: TINTA, marginTop: 4 },
  gan: { fontSize: 14, fontWeight: 700, color: MORADO, marginTop: 4 },
  foot: { marginTop: 18, borderTopWidth: 1, borderTopColor: LINEA, paddingTop: 10, fontSize: 8.5, color: GRIS, lineHeight: 1.45 },
  pie: { position: 'absolute', bottom: 18, left: 38, right: 38, fontSize: 7.5, color: CLARO, textAlign: 'center' },
});
const W_CLI = ['46%', '16%', '12%', '26%'];
const W_INT = ['38%', '10%', '17%', '17%', '18%'];

const PDFEspecial = ({ data }) => {
  const d = data || {}; const cli = d.cara !== 'int'; const mk = d.marca || {};
  const logo = mk.logo_data || null;
  return (
    <Document>
      <Page size="LETTER" style={s.page}>
        <View style={s.top}>
          <View style={{ maxWidth: '58%' }}>
            {logo ? <Image src={logo} style={s.logo} /> : null}
            {mk.mostrar_marca !== false && mk.nombre ? <Text style={s.marca}>{mk.nombre}</Text> : (!logo ? <Text style={[s.marca, { color: CLARO }]}>Nota de reserva</Text> : null)}
            <Text style={s.sub}>{d.grupo || '—'}{d.contacto ? ` · ${d.contacto}` : ''}{d.personas ? ` · ${d.personas} personas` : ''}</Text>
            <Text style={s.sub}>{f(d.llegada)} a {f(d.salida)}</Text>
          </View>
          <View style={s.der}>
            <Text style={s.tag}>{cli ? 'Para el cliente' : 'Interno · contabilidad'}</Text>
            <Text style={s.folio}>{d.folio}</Text>
            {d.version ? <Text style={s.sub}>Versión {d.version}</Text> : null}
            {cli && mk.mostrar_contacto !== false && (mk.telefono || mk.correo) ? <Text style={s.contacto}>{[mk.telefono, mk.correo].filter(Boolean).join('\n')}</Text> : null}
          </View>
        </View>

        {cli ? (
          <>
            <View style={s.th}><Text style={[s.thc, { width: W_CLI[0] }]}>Producto</Text><Text style={[s.thc, { width: W_CLI[1] }]}>Fecha</Text><Text style={[s.thc, s.r, { width: W_CLI[2] }]}>Cant.</Text><Text style={[s.thc, s.r, { width: W_CLI[3] }]}>Importe</Text></View>
            {(d.renglones || []).map((r, i) => (
              <View key={i} style={s.tr}><Text style={[s.td, { width: W_CLI[0] }]}>{r.producto}</Text><Text style={[s.td, { width: W_CLI[1], color: GRIS }]}>{f(r.fecha)}</Text><Text style={[s.td, s.r, { width: W_CLI[2] }]}>{r.cantidad}</Text><Text style={[s.td, s.r, { width: W_CLI[3] }]}>{m(r.venta)}</Text></View>
            ))}
            <View style={s.tot}>
              {(d.conceptos_cli || []).map((k, i) => <Text key={i} style={s.linea}>{k.nombre} {m(k.importe)}</Text>)}
              <Text style={s.grande}>Total {m(d.total)}</Text>
              <Text style={s.linea}>Pagado {m(d.pagado)} · Saldo {m(d.saldo)}</Text>
            </View>
            {(mk.terminos || mk.pie) ? <Text style={s.foot}>{[mk.terminos, mk.pie].filter(Boolean).join('\n')}</Text> : null}
          </>
        ) : (
          <>
            <View style={s.th}><Text style={[s.thc, { width: W_INT[0] }]}>Producto</Text><Text style={[s.thc, s.r, { width: W_INT[1] }]}>Cant.</Text><Text style={[s.thc, s.r, { width: W_INT[2] }]}>Neto</Text><Text style={[s.thc, s.r, { width: W_INT[3] }]}>Venta</Text><Text style={[s.thc, s.r, { width: W_INT[4] }]}>Margen</Text></View>
            {(d.renglones || []).map((r, i) => (
              <View key={i} style={s.tr}><Text style={[s.td, { width: W_INT[0] }]}>{r.producto}{r.fecha ? ` · ${f(r.fecha)}` : ''}</Text><Text style={[s.td, s.r, { width: W_INT[1] }]}>{r.cantidad}</Text><Text style={[s.td, s.r, { width: W_INT[2] }]}>{m(r.neto)}</Text><Text style={[s.td, s.r, { width: W_INT[3] }]}>{m(r.venta)}</Text><Text style={[s.td, s.r, { width: W_INT[4], color: r.margen < 0 ? MAL : OK }]}>{m(r.margen)}</Text></View>
            ))}
            <View style={s.tot}>
              <Text style={s.linea}>Total al grupo {m(d.total)}</Text>
              <Text style={s.linea}>Impuestos {m(d.impuestos)}</Text>
              <Text style={s.linea}>Neto a proveedores {m(-d.neto)}</Text>
              {Object.keys(d.por_quien || {}).map((w) => <Text key={w} style={s.linea}>Comisión {w} {m(-d.por_quien[w])}</Text>)}
              <Text style={s.gan}>Ganancia Cancun100 {m(d.ganancia)}</Text>
              <Text style={s.linea}>Pagado {m(d.pagado)} · Saldo {m(d.saldo)}</Text>
            </View>
            {(d.pagos || []).length > 0 ? (
              <>
                <View style={s.th}><Text style={[s.thc, { width: '18%' }]}>Fecha</Text><Text style={[s.thc, { width: '34%' }]}>Concepto</Text><Text style={[s.thc, { width: '20%' }]}>Método</Text><Text style={[s.thc, s.r, { width: '28%' }]}>En MXN</Text></View>
                {d.pagos.map((p, i) => <View key={i} style={s.tr}><Text style={[s.td, { width: '18%' }]}>{f(p.fecha)}</Text><Text style={[s.td, { width: '34%' }]}>{p.concepto}</Text><Text style={[s.td, { width: '20%', color: GRIS }]}>{p.metodo} · {p.moneda}{p.estado_revision === 'aceptado' ? ' · aceptado' : ''}</Text><Text style={[s.td, s.r, { width: '28%' }]}>{m(p.monto_mxn)}</Text></View>)}
              </>
            ) : null}
            <Text style={s.foot}>{d.version ? `Versión ${d.version}` : 'Versión actual'} · {d.versiones_previas || 0} versiones guardadas. Documento interno: nunca se envía al cliente.</Text>
          </>
        )}
        <Text style={s.pie} fixed>{d.folio} · {cli ? 'Nota de reserva' : 'Interna'} · {String(d.fecha || '').slice(0, 10)}</Text>
      </Page>
    </Document>
  );
};
export default PDFEspecial;
